import React, { useState, useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert,
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Divider,
  Chip } from
'@mui/material';
import {
  GetApp as GetAppIcon,
  PhoneAndroid as PhoneIcon,
  Computer as ComputerIcon,
  Autorenew as AutorenewIcon,
  CheckCircle as CheckCircleIcon } from
'@mui/icons-material';import { useThemeSettings } from "../contexts/ThemeContext";

const InstallPWA = () => {const { t } = useThemeSettings();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallDialog, setShowInstallDialog] = useState(false);
  const [showMobileInstructions, setShowMobileInstructions] = useState(false);
  const [showStartupInstructions, setShowStartupInstructions] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [platform, setPlatform] = useState('unknown');
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Detect platform
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    let detectedPlatform = 'desktop';

    if (/android/i.test(userAgent)) {
      detectedPlatform = 'android';
    } else if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
      detectedPlatform = 'ios';
    } else if (/windows/i.test(userAgent)) {
      detectedPlatform = 'windows';
    } else if (/mac/i.test(userAgent)) {
      detectedPlatform = 'mac';
    }

    setPlatform(detectedPlatform);

    // Check if the app is already installed
    const isAppInstalled = window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone ||
    document.referrer.includes('android-app://');

    setIsInstalled(isAppInstalled);

    if (isAppInstalled) {
      return; // Don't show install prompt if already installed
    }

    const handler = (e) => {
      // Prevent Chrome 76+ from automatically showing the prompt
      e.preventDefault();
      // Stash the event so it can be triggered later
      setDeferredPrompt(e);
      // Show install dialog after a short delay
      setTimeout(() => {
        setShowInstallDialog(true);
      }, 3000);
    };

    // Add event listener for beforeinstallprompt event
    window.addEventListener('beforeinstallprompt', handler);

    // Monitor when the app gets installed
    window.addEventListener('appinstalled', () => {
      //console.log('PWA was installed');
      setShowSuccess(true);
      setDeferredPrompt(null);
      setIsInstalled(true);

      // Show startup instructions after successful install on Windows
      if (detectedPlatform === 'windows') {
        setTimeout(() => {
          setShowStartupInstructions(true);
        }, 2000);
      }
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      // If no prompt available, show platform-specific instructions
      if (platform === 'ios' || platform === 'android') {
        setShowMobileInstructions(true);
      }
      setShowInstallDialog(false);
      return;
    }

    // Show the browser install prompt
    deferredPrompt.prompt();

    // Wait for the user to respond to the prompt
    const { outcome } = await deferredPrompt.userChoice;
    //console.log(`User response to the install prompt: ${outcome}`);

    // Clear the deferredPrompt for next time
    setDeferredPrompt(null);
    setShowInstallDialog(false);

    if (outcome === 'accepted') {
      setShowSuccess(true);
    }
  };

  const handleShowMobileInstructions = () => {
    setShowInstallDialog(false);
    setShowMobileInstructions(true);
  };

  const handleShowStartupInstructions = () => {
    setShowStartupInstructions(true);
  };

  // Debug function to manually show the dialog (for testing)
  const debugShowDialog = () => {
    if (!window.matchMedia('(display-mode: standalone)').matches) {
      setShowInstallDialog(true);
    }
  };

  const getMobileInstructions = () => {const { t } = useThemeSettings();
    if (platform === 'ios') {
      return (
        <Stepper orientation="vertical">
          <Step active>
            <StepLabel>{t ? t("InstallPWA.tap_share_button") : "Tap the Share button"}</StepLabel>
            <StepContent>
              <Typography variant="body2">{t ? t("InstallPWA.look_share_icon") : "Look for the Share icon"}
                <strong>{t ? t("InstallPWA.square_arrow_pointing_up") : "(square with arrow pointing up)"}</strong>{t ? t("InstallPWA.safaris_toolbar_bottom") : "in Safari's toolbar at the bottom of the screen"}
              </Typography>
            </StepContent>
          </Step>
          <Step active>
            <StepLabel>{t ? t("InstallPWA.scroll_find_add_home_screen") : "Scroll and find \"Add to Home Screen\""}</StepLabel>
            <StepContent>
              <Typography variant="body2">{t ? t("InstallPWA.scroll_down_share_menu_tap") : "Scroll down in the share menu and tap"}
                <strong>{t ? t("InstallPWA.add_home_screen") : "\"Add to Home Screen\""}</strong>
              </Typography>
            </StepContent>
          </Step>
          <Step active>
            <StepLabel>{t ? t("InstallPWA.tap_add") : "Tap \"Add\""}</StepLabel>
            <StepContent>
              <Typography variant="body2">{t ? t("InstallPWA.confirm_tapping") : "Confirm by tapping"}
                <strong>{t ? t("InstallPWA.add") : "\"Add\""}</strong>{t ? t("InstallPWA.top_right_corner_app_will_now") : "in the top right corner. The app will now appear on your home screen."}
              </Typography>
            </StepContent>
          </Step>
        </Stepper>);

    } else if (platform === 'android') {
      return (
        <Stepper orientation="vertical">
          <Step active>
            <StepLabel>{t ? t("InstallPWA.tap_menu_button") : "Tap the Menu button"}</StepLabel>
            <StepContent>
              <Typography variant="body2">{t ? t("InstallPWA.look_threedot_menu") : "Look for the three-dot menu"}
                <strong>(⋮)</strong>{t ? t("InstallPWA.top_right_corner_chrome") : "in the top right corner of Chrome"}
              </Typography>
            </StepContent>
          </Step>
          <Step active>
            <StepLabel>{t ? t("InstallPWA.select_add_home_screen_install_app") : "Select \"Add to Home screen\" or \"Install app\""}</StepLabel>
            <StepContent>
              <Typography variant="body2">{t ? t("InstallPWA.tap") : "Tap on"}
                <strong>{t ? t("InstallPWA.add_home_screen_1") : "\"Add to Home screen\""}</strong>{t ? t("InstallPWA.text_0") : "or"}<strong>{t ? t("InstallPWA.install_app") : "\"Install app\""}</strong>{t ? t("InstallPWA.option") : "option"}
              </Typography>
            </StepContent>
          </Step>
          <Step active>
            <StepLabel>{t ? t("InstallPWA.confirm_installation") : "Confirm installation"}</StepLabel>
            <StepContent>
              <Typography variant="body2">{t ? t("InstallPWA.tap_1") : "Tap"}
                <strong>{t ? t("InstallPWA.add") : "\"Add\""}</strong>{t ? t("InstallPWA.text_0") : "or"}<strong>{t ? t("InstallPWA.install") : "\"Install\""}</strong>{t ? t("InstallPWA.app_will_appear_your_home_screen") : ". The app will appear on your home screen."}
              </Typography>
            </StepContent>
          </Step>
        </Stepper>);

    }

    return (
      <Typography variant="body2">{t ? t("InstallPWA.install_your_mobile_device") : "To install on your mobile device:"}

        <br />{t ? t("InstallPWA.1_open_this_page_your_mobile_browser") : "1. Open this page in your mobile browser (Chrome/Safari)"}
        <br />{t ? t("InstallPWA.2_look_add_home_screen_ins") : "2. Look for the \"Add to Home Screen\" or \"Install\" option in your browser menu"}
        <br />{t ? t("InstallPWA.3_follow_prompts_add_app_y") : "3. Follow the prompts to add the app to your device"}
      </Typography>);

  };

  return (
    <>
      {/* Main Install Dialog */}
      <Dialog
        open={showInstallDialog}
        onClose={() => setShowInstallDialog(false)}
        aria-labelledby="install-dialog-title"
        maxWidth="sm"
        fullWidth>
        
        <DialogTitle id="install-dialog-title">
          <Box display="flex" alignItems="center" gap={1}>
            <GetAppIcon color="primary" />{t ? t("InstallPWA.install_caro_management_controller") : "Install CARO Management Controller"}

          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>{t ? t("InstallPWA.install_our_app_faster_access_better") : "Install our app for faster access, better performance, and offline capabilities."}

          </DialogContentText>

          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            <Chip icon={<CheckCircleIcon />} label={t ? t("InstallPWA.auto_boot_startup") : "Auto boot on startup"} size="small" />
            <Chip icon={<CheckCircleIcon />} label={t ? t("InstallPWA.fast_loading") : "Fast loading"} size="small" />
            <Chip icon={<CheckCircleIcon />} label={t ? t("InstallPWA.desktopmobile_support") : "Desktop/Mobile support"} size="small" />
          </Box>

          {(platform === 'ios' || platform === 'android') &&
          <Box sx={{ mt: 2, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
              <Typography variant="body2" color="info.contrastText">
                <PhoneIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />{t ? t("InstallPWA.mobile_device_detected_view_installation") : "Mobile device detected. View installation instructions for your device."}

            </Typography>
            </Box>
          }
        </DialogContent>
        <DialogActions>
          {(platform === 'ios' || platform === 'android') &&
          <Button
            onClick={handleShowMobileInstructions}
            color="primary"
            startIcon={<PhoneIcon />}>{t ? t("InstallPWA.mobile_instructions") : "Mobile Instructions"}


          </Button>
          }
          <Button onClick={() => setShowInstallDialog(false)} color="primary">{t ? t("InstallPWA.not_now") : "Not now"}

          </Button>
          {deferredPrompt &&
          <Button onClick={handleInstallClick} color="primary" variant="contained">{t ? t("InstallPWA.install_1") : "Install"}

          </Button>
          }
        </DialogActions>
      </Dialog>

      {/* Mobile Instructions Dialog */}
      <Dialog
        open={showMobileInstructions}
        onClose={() => setShowMobileInstructions(false)}
        aria-labelledby="mobile-instructions-title"
        maxWidth="sm"
        fullWidth>
        
        <DialogTitle id="mobile-instructions-title">
          <Box display="flex" alignItems="center" gap={1}>
            <PhoneIcon color="primary" />{t ? t("InstallPWA.install_2") : "Install on"}
            {platform === 'ios' ? 'iOS' : 'Android'}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom fontWeight="bold">{t ? t("InstallPWA.follow_these_steps_install_caro_yo") : "Follow these steps to install CARO on your device:"}

          </Typography>
          <Box sx={{ mt: 2 }}>
            {getMobileInstructions()}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowMobileInstructions(false)} color="primary" variant="contained">{t ? t("InstallPWA.got_it") : "Got it"}

          </Button>
        </DialogActions>
      </Dialog>

      {/* Windows Startup Instructions Dialog */}
      <Dialog
        open={showStartupInstructions}
        onClose={() => setShowStartupInstructions(false)}
        aria-labelledby="startup-instructions-title"
        maxWidth="md"
        fullWidth>
        
        <DialogTitle id="startup-instructions-title">
          <Box display="flex" alignItems="center" gap={1}>
            <AutorenewIcon color="primary" />{t ? t("InstallPWA.launch_caro_windows_startup") : "Launch CARO on Windows Startup"}

          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom fontWeight="bold">{t ? t("InstallPWA.make_caro_launch_automatically_when_w") : "To make CARO launch automatically when Windows starts:"}

          </Typography>

          <Stepper orientation="vertical" sx={{ mt: 2 }}>
            <Step active>
              <StepLabel>{t ? t("InstallPWA.open_windows_settings") : "Open Windows Settings"}</StepLabel>
              <StepContent>
                <Typography variant="body2">{t ? t("InstallPWA.press") : "Press"}
                  <strong>{t ? t("InstallPWA.windows_i") : "Windows + I"}</strong>{t ? t("InstallPWA.open_settings_search_settings") : "to open Settings, or search for \"Settings\" in the Start menu"}
                </Typography>
              </StepContent>
            </Step>

            <Step active>
              <StepLabel>{t ? t("InstallPWA.navigate_apps_startup") : "Navigate to Apps \u2192 Startup"}</StepLabel>
              <StepContent>
                <Typography variant="body2">{t ? t("InstallPWA.click") : "Click on"}
                  <strong>{t ? t("InstallPWA.apps") : "\"Apps\""}</strong>{t ? t("InstallPWA.left_sidebar_then_select") : "in the left sidebar, then select"}<strong>{t ? t("InstallPWA.startup") : "\"Startup\""}</strong>
                </Typography>
              </StepContent>
            </Step>

            <Step active>
              <StepLabel>{t ? t("InstallPWA.find_caro_management_controller") : "Find CARO Management Controller"}</StepLabel>
              <StepContent>
                <Typography variant="body2">{t ? t("InstallPWA.look") : "Look for"}
                  <strong>{t ? t("InstallPWA.caro_management_controller") : "\"CARO Management Controller\""}</strong>{t ? t("InstallPWA.text_0") : "or"}<strong>{t ? t("InstallPWA.caro") : "\"CARO\""}</strong>{t ? t("InstallPWA.list_apps") : "in the list of apps"}
                </Typography>
              </StepContent>
            </Step>

            <Step active>
              <StepLabel>{t ? t("InstallPWA.toggle_it") : "Toggle it ON"}</StepLabel>
              <StepContent>
                <Typography variant="body2">{t ? t("InstallPWA.switch_toggle") : "Switch the toggle to"}
                  <strong>ON</strong>{t ? t("InstallPWA.caro_will_now_launch_automatically_when") : ". CARO will now launch automatically when you start Windows."}
                </Typography>
              </StepContent>
            </Step>
          </Stepper>

          <Divider sx={{ my: 2 }} />

          <Typography variant="body2" color="text.secondary">
            <strong>{t ? t("InstallPWA.alternative_method_using_task_scheduler") : "Alternative method using Task Scheduler:"}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{t ? t("InstallPWA.1_search_task_scheduler_windows") : "1. Search for \"Task Scheduler\" in Windows"}

            <br />{t ? t("InstallPWA.2_click_create_basic_task") : "2. Click \"Create Basic Task\""}
            <br />{t ? t("InstallPWA.3_name_it_caro_startup") : "3. Name it \"CARO Startup\""}
            <br />{t ? t("InstallPWA.4_set_trigger_when_i_log") : "4. Set trigger to \"When I log on\""}
            <br />{t ? t("InstallPWA.5_action_start_program") : "5. Action: \"Start a program\""}
            <br />{t ? t("InstallPWA.6_program_your_default_browser") : "6. Program: Your default browser with the URL to CARO"}
            <br />{t ? t("InstallPWA.example") : "Example:"}<code>chrome.exe --app=https://portal.caro.care</code>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowStartupInstructions(false)} color="primary" variant="contained">{t ? t("InstallPWA.got_it") : "Got it"}

          </Button>
        </DialogActions>
      </Dialog>

      {/* Success notification */}
      <Snackbar
        open={showSuccess}
        autoHideDuration={6000}
        onClose={() => setShowSuccess(false)}>
        
        <Alert onClose={() => setShowSuccess(false)} severity="success" sx={{ width: '100%' }}>{t ? t("InstallPWA.caro_management_controller_installed_suc") : "CARO Management Controller installed successfully!"}

        </Alert>
      </Snackbar>

      {/* Debug button - only visible in development */}
      {process.env.NODE_ENV === 'development' && !isInstalled &&
      <Box position="fixed" bottom={16} right={16} zIndex={1000}>
          <Button
          variant="contained"
          color="primary"
          onClick={debugShowDialog}
          size="small"
          startIcon={<ComputerIcon />}>{t ? t("InstallPWA.install_desktop") : "Install on Desktop"}


        </Button>
        </Box>
      }
    </>);

};

export default InstallPWA;